// Front-end mirror of api/src/permissions.ts. Both files must stay in sync —
// the API converts between this nested shape and the flat
// role_permissions/user_permissions storage on every read/write of
// /api/users/:id/access.

export type ModuleActionKey =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "cancel";

export interface ModuleDef {
  key: string;
  label: string;
  // Action → flat permission string. Missing entries mean the action does not
  // apply for that module and is hidden in the matrix UI.
  actions: Partial<Record<ModuleActionKey, string>>;
}

// Order here drives row order in the Module Access matrix.
export const MODULES: ModuleDef[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    actions: { view: "dashboard.view" },
  },
  {
    key: "quotations",
    label: "Quotations",
    actions: {
      view: "quotes.read",
      edit: "quotes.write",
      cancel: "quotes.cancel",
    },
  },
  {
    key: "receipts",
    label: "Receipts",
    actions: {
      view: "receipts.read",
      create: "receipts.create",
      edit: "receipts.write",
      cancel: "receipts.cancel",
    },
  },
  {
    key: "waybills",
    label: "Waybills",
    actions: {
      view: "waybills.read",
      edit: "waybills.write",
      cancel: "waybills.cancel",
    },
  },
  {
    key: "sales_invoices",
    label: "Sales Invoices",
    actions: {
      view: "sales_invoices.read",
      create: "sales_invoices.create",
      edit: "sales_invoices.write",
      cancel: "sales_invoices.cancel",
    },
  },
  {
    key: "production_orders",
    label: "Production Orders",
    actions: {
      view: "production_orders.read",
      edit: "production_orders.write",
      cancel: "production_orders.status",
    },
  },
  {
    key: "customers",
    label: "Entities",
    actions: {
      view: "customers.read",
      edit: "customers.write",
      delete: "customers.delete",
    },
  },
  {
    key: "products",
    label: "Inventory",
    actions: {
      view: "products.read",
      edit: "products.write",
      delete: "products.delete",
    },
  },
  {
    key: "inventory_attributes",
    label: "Inventory Attributes",
    actions: {
      view: "products.read",
      edit: "inventory.attributes.manage",
      delete: "inventory.attributes.manage",
    },
  },
  { key: "reports", label: "Reports", actions: { view: "reports.view" } },
  { key: "backup", label: "Backup", actions: { view: "backup.view" } },
  { key: "settings", label: "Settings", actions: { view: "settings.read" } },
  { key: "finance", label: "Finance Overview", actions: { view: "finance.view" } },
  {
    key: "general_ledger",
    label: "General Ledger",
    actions: {
      view: "gl.view",
      edit: "gl.write",
      delete: "gl.close",
      cancel: "gl.post",
    },
  },
  {
    key: "customer_ledger",
    label: "Customer Ledger",
    actions: {
      view: "customer_ledger.read",
      edit: "customer_ledger.write",
    },
  },
];

// Column order in the Module Access matrix. "create" sits between view and
// edit so document rows read view | create | edit | delete | cancel.
// Modules that don't define a "create" mapping render a blank "—" cell, the
// same way the delete/cancel columns are already optional per module.
export const ACTION_COLUMNS: ModuleActionKey[] = [
  "view",
  "create",
  "edit",
  "delete",
  "cancel",
];

export function moduleDef(moduleKey: string): ModuleDef | undefined {
  return MODULES.find((m) => m.key === moduleKey);
}

export function modulePermission(
  moduleKey: string,
  action: ModuleActionKey,
): string | undefined {
  return moduleDef(moduleKey)?.actions[action];
}

export interface NestedAccess {
  [moduleKey: string]: Partial<Record<ModuleActionKey, boolean>>;
}

/** Build an empty {module: {action: false}} matrix that covers every module. */
export function emptyAccess(): NestedAccess {
  const out: NestedAccess = {};
  for (const m of MODULES) {
    const row: Partial<Record<ModuleActionKey, boolean>> = {};
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      row[a] = false;
    }
    out[m.key] = row;
  }
  return out;
}

/** Coerce a partial access object from the API into a full matrix. */
export function normalizeAccess(
  raw: Partial<NestedAccess> | undefined | null,
): NestedAccess {
  const base = emptyAccess();
  if (!raw) return base;
  for (const m of MODULES) {
    const row = raw[m.key] ?? {};
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      base[m.key]![a] = row[a] === true;
    }
  }
  return base;
}

// Compute canAccess(module) from a user's flat permissions list. The user
// can "access" a module if they have its `view` permission (or the
// administrator role short-circuit handled by the caller).
export function canAccessModule(
  moduleKey: string,
  permissions: string[] | undefined,
): boolean {
  const view = modulePermission(moduleKey, "view");
  if (!view || !permissions) return false;
  return permissions.includes(view);
}

// Mirror of api/src/permissions.ts -> SUPPORT_READ_DERIVATIONS.
//
// The document forms now load their pickers via dedicated, document-scoped
// `/form-support` endpoints (e.g. `/api/quotations/form-support`), gated by
// the same `<doc>.read|write` actions the form already requires. As a
// result, granting a primary document module is sufficient — the admin
// never has to also tick Customers / Products / Quotations / Receipts.
//
// This map is therefore intentionally empty: no cascade fires, no toast is
// emitted, and the matrix only ever stores what the admin explicitly
// ticked. The constant is retained for API parity with the backend and so
// any future "primary action → support read" dependencies (outside the
// document-form picker case) can be re-added in one place.
export const SUPPORT_READ_DERIVATIONS: Record<string, string[]> = {
  "quotes.cancel": ["quotes.read"],
  "receipts.cancel": ["receipts.read"],
  "waybills.cancel": ["waybills.read"],
  "sales_invoices.cancel": ["sales_invoices.read"],
};

// Flatten a nested matrix into the actions it explicitly grants. The matrix
// is the source of truth for the admin's choices — nothing is derived.
export function flattenAccess(matrix: NestedAccess): string[] {
  const out = new Set<string>();
  for (const m of MODULES) {
    const row = matrix[m.key];
    if (!row) continue;
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      const flat = m.actions[a];
      if (flat && row[a] === true) out.add(flat);
    }
  }
  return Array.from(out);
}

export interface MissingSupportRead {
  // The flat permission string the user is missing (e.g. "customers.read").
  flat: string;
  // Friendly module label + action key for the missing permission so the UI
  // can render "Customers (view)" without re-doing the lookup.
  moduleLabel: string;
  actionKey: ModuleActionKey;
  // Friendly labels for the granted actions that need this read.
  neededBy: { moduleLabel: string; actionKey: ModuleActionKey }[];
}

function lookupFlat(
  flat: string,
): { moduleLabel: string; actionKey: ModuleActionKey } | null {
  for (const m of MODULES) {
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      if (m.actions[a] === flat) {
        return { moduleLabel: m.label, actionKey: a };
      }
    }
  }
  return null;
}

// Reverse lookup that returns the module key (not label) so the cascade
// helper can write back into the nested matrix.
function lookupFlatCell(
  flat: string,
): { moduleKey: string; actionKey: ModuleActionKey } | null {
  for (const m of MODULES) {
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      if (m.actions[a] === flat) {
        return { moduleKey: m.key, actionKey: a };
      }
    }
  }
  return null;
}

export interface CascadeTick {
  moduleKey: string;
  moduleLabel: string;
  actionKey: ModuleActionKey;
  flat: string;
}

export interface CascadeResult {
  matrix: NestedAccess;
  cascaded: CascadeTick[];
}

// Apply a single matrix-cell toggle and, when ticking ON, also tick the
// supporting reads declared in SUPPORT_READ_DERIVATIONS. Returns the next
// matrix plus the list of cells that the cascade flipped on (so the UI can
// surface a short "auto-included" toast). Pure function — never mutates
// `prev`. Unticking is never cascaded; the admin's negative actions are
// always taken at face value.
export function applyCascade(
  prev: NestedAccess,
  moduleKey: string,
  actionKey: ModuleActionKey,
  newValue: boolean,
): CascadeResult {
  const next: NestedAccess = {};
  for (const m of MODULES) {
    const src = prev[m.key] ?? {};
    const row: Partial<Record<ModuleActionKey, boolean>> = {};
    for (const a of Object.keys(m.actions) as ModuleActionKey[]) {
      row[a] = src[a] === true;
    }
    next[m.key] = row;
  }

  const targetRow = next[moduleKey];
  if (!targetRow) return { matrix: next, cascaded: [] };
  targetRow[actionKey] = newValue;

  if (!newValue) return { matrix: next, cascaded: [] };

  const flat = modulePermission(moduleKey, actionKey);
  if (!flat) return { matrix: next, cascaded: [] };

  const deps = SUPPORT_READ_DERIVATIONS[flat] ?? [];
  const cascaded: CascadeTick[] = [];
  for (const dep of deps) {
    const cell = lookupFlatCell(dep);
    if (!cell) continue;
    if (cell.moduleKey === moduleKey && cell.actionKey === actionKey) continue;
    const depRow = next[cell.moduleKey];
    if (!depRow) continue;
    if (depRow[cell.actionKey] === true) continue;
    depRow[cell.actionKey] = true;
    const def = moduleDef(cell.moduleKey);
    cascaded.push({
      moduleKey: cell.moduleKey,
      moduleLabel: def?.label ?? cell.moduleKey,
      actionKey: cell.actionKey,
      flat: dep,
    });
  }
  return { matrix: next, cascaded };
}

// Human-readable label for a flat permission, e.g. "Customers (view)".
// Used in 403-hint toasts on the front end.
export function describeFlatPermission(flat: string): string | null {
  const info = lookupFlat(flat);
  if (!info) return null;
  return `${info.moduleLabel} (${info.actionKey})`;
}

// Shape of the structured 403 payload the API now returns.
export interface ForbiddenBody {
  error: string;
  required?: string;
  hint?: string;
}

// Pull the friendly hint out of an ApiError-style payload, falling back to
// `describeFlatPermission` for the legacy `{ required }`-only shape and to
// the generic message when no permission info is available.
export function permissionHintFromBody(
  body: unknown,
  fallback: string,
): string {
  if (!body || typeof body !== "object") return fallback;
  const b = body as ForbiddenBody;
  if (typeof b.hint === "string" && b.hint.length > 0) return b.hint;
  if (typeof b.required === "string") {
    const friendly = describeFlatPermission(b.required);
    if (friendly) {
      return `Ask your administrator to grant ${friendly} in your Module Access.`;
    }
    return `Ask your administrator to grant ${b.required} in your Module Access.`;
  }
  return fallback;
}

// Inspect the currently-ticked matrix and return the support reads that one
// or more granted actions will need at runtime but which the admin has not
// also ticked. The UI surfaces these as a save-time warning so the admin can
// choose to grant them or accept the resulting 403s.
export function missingSupportReadsForMatrix(
  matrix: NestedAccess,
): MissingSupportRead[] {
  const granted = new Set(flattenAccess(matrix));
  const missing = new Map<
    string,
    Set<string>
  >();
  for (const action of granted) {
    const deps = SUPPORT_READ_DERIVATIONS[action] ?? [];
    for (const dep of deps) {
      if (granted.has(dep)) continue;
      if (!missing.has(dep)) missing.set(dep, new Set());
      missing.get(dep)!.add(action);
    }
  }
  const out: MissingSupportRead[] = [];
  for (const [flat, neededBy] of missing) {
    const info = lookupFlat(flat);
    if (!info) continue;
    const neededByLabels: { moduleLabel: string; actionKey: ModuleActionKey }[] = [];
    for (const src of Array.from(neededBy).sort()) {
      const srcInfo = lookupFlat(src);
      if (srcInfo) neededByLabels.push(srcInfo);
    }
    out.push({
      flat,
      moduleLabel: info.moduleLabel,
      actionKey: info.actionKey,
      neededBy: neededByLabels,
    });
  }
  out.sort((a, b) =>
    a.moduleLabel.localeCompare(b.moduleLabel) ||
    a.actionKey.localeCompare(b.actionKey),
  );
  return out;
}
